<template>
  <div class="login-page tech-login">
    <div class="login-grid-bg"></div>
    <div class="login-glow login-glow-one"></div>
    <div class="login-glow login-glow-two"></div>
    <div class="login-glow login-glow-three"></div>

    <div class="login-shell">

      <!-- ── LEFT: LOGIN PANEL ── -->
      <section class="login-panel modern-card tech-login-card">
        <router-link class="back-home" to="/home">
          <i class="el-icon-arrow-left"></i>
          返回首页
        </router-link>

        <div class="login-brand">
          <div class="brand-badge"><span></span>智训立方</div>
          <h1>登录智能化<br />情景交互学习平台</h1>
          <p>进入课程学习、案例训练、情景体验与学习管理空间。</p>
        </div>

        <el-form
          ref="loginForm"
          :model="loginForm"
          :rules="loginRules"
          label-position="top"
          class="login-form"
        >
          <el-form-item prop="username" class="login-item" label="账号">
            <el-input
              v-model="loginForm.username"
              type="text"
              auto-complete="off"
              prefix-icon="el-icon-user"
              placeholder="请输入账号 / 手机号 / 邮箱"
            />
          </el-form-item>

          <el-form-item prop="password" class="login-item" label="密码">
            <el-input
              v-model="loginForm.password"
              type="password"
              auto-complete="off"
              prefix-icon="el-icon-lock"
              placeholder="请输入密码"
              show-password
              @keyup.enter.native="handleLogin"
            />
          </el-form-item>

          <el-form-item
            prop="code"
            class="login-item captcha-item"
            label="验证码"
            v-if="captchaEnabled"
          >
            <div class="captcha-row">
              <el-input
                v-model="loginForm.code"
                auto-complete="off"
                prefix-icon="el-icon-key"
                placeholder="请输入验证码"
                @keyup.enter.native="handleLogin"
              />
              <button type="button" class="captcha-img" @click="getCode" title="点击刷新验证码">
                <img :src="codeUrl" alt="验证码" />
              </button>
            </div>
          </el-form-item>

          <div class="form-assist">
            <el-checkbox v-model="loginForm.rememberMe">记住账号</el-checkbox>
            <span>忘记密码请联系平台管理员</span>
          </div>

          <el-form-item class="submit-item">
            <el-button
              :loading="loading"
              size="large"
              type="primary"
              class="login-submit-btn"
              @click.native.prevent="handleLogin"
            >
              <span v-if="!loading">进入平台</span>
              <span v-else>登录中...</span>
            </el-button>
          </el-form-item>
        </el-form>

        <div class="login-note">
          <i class="el-icon-info"></i>
          <span>建议使用 Chrome / Edge 浏览器访问，获得更稳定的情景案例体验。</span>
        </div>
      </section>

      <!-- ── RIGHT: FLOW VISUALIZATION ── -->
      <section class="login-visual">

        <!-- Top copy -->
        <div class="visual-top">
          <div class="visual-eyebrow"><span></span>Scenario-based Learning Platform</div>
          <h2>智能化情景交互学习<br />完整学习体验路径</h2>
          <p>从场景导入到教学管理，每个环节都有数字化支撑。</p>
        </div>

        <!-- Flow visualization -->
        <div class="flow-wrap">
          <!-- Background decorative elements -->
          <div class="flow-bg-grid"></div>
          <div class="flow-bg-glow"></div>

          <!-- Flow steps -->
          <div class="flow-steps">
            <div
              v-for="(step, index) in flowSteps"
              :key="step.title"
              class="flow-step"
              :class="{ 'flow-step--active': index === activeStep }"
              @mouseenter="activeStep = index"
              @mouseleave="activeStep = 0"
            >
              <!-- Left: icon column -->
              <div class="fs-icon-col">
                <div class="fs-icon-wrap">
                  <i :class="step.icon"></i>
                </div>
                <!-- connector line (not on last item) -->
                <div v-if="index < flowSteps.length - 1" class="fs-connector">
                  <div class="fs-connector-line"></div>
                  <div class="fs-connector-dot"></div>
                </div>
              </div>

              <!-- Right: content -->
              <div class="fs-content">
                <div class="fs-header">
                  <span class="fs-step-num">0{{ index + 1 }}</span>
                  <h3>{{ step.title }}</h3>
                  <span class="fs-status" :class="step.statusColor">{{ step.status }}</span>
                </div>
                <p>{{ step.desc }}</p>
                <div class="fs-tags">
                  <span v-for="tag in step.tags" :key="tag">{{ tag }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Right side: data panel -->
          <div class="flow-panel">
            <div class="fp-header">
              <span class="fp-dot"></span>
              平台状态
            </div>
            <div class="fp-metric" v-for="m in panelMetrics" :key="m.label">
              <span class="fp-label">{{ m.label }}</span>
              <div class="fp-bar-wrap">
                <div class="fp-bar" :style="{ width: m.pct + '%' }"></div>
              </div>
              <span class="fp-val">{{ m.val }}</span>
            </div>
            <div class="fp-divider"></div>
            <div class="fp-status-list">
              <div class="fp-status-row" v-for="s in panelStatus" :key="s.name">
                <span :class="['fp-status-dot', s.color]"></span>
                <span class="fp-status-name">{{ s.name }}</span>
                <span class="fp-status-tag">{{ s.tag }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Bottom feature strip -->
        <div class="feature-strip modern-card">
          <div v-for="item in featureList" :key="item.title" class="feature-item">
            <div class="fi-icon"><i :class="item.icon"></i></div>
            <div class="fi-body">
              <strong>{{ item.title }}</strong>
              <span>{{ item.desc }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { getCodeImg, login } from "@/api/index.js";
import Cookies from "js-cookie";
import { encrypt, decrypt } from "@/utils/jsencrypt.js";
import { setToken } from "@/utils/auth.js";

export default {
  name: "Login",
  data() {
    return {
      codeUrl: "",
      loginForm: {
        username: "",
        password: "",
        rememberMe: false,
        code: "",
        uuid: "",
      },
      activeStep: 0,
      flowSteps: [
        {
          icon: "el-icon-location-outline",
          title: "场景导入",
          status: "已就绪",
          statusColor: "s-green",
          desc: "以真实应急场景为背景，建立任务情境，激发学习动机。",
          tags: ["情景化", "真实场景", "任务背景"],
        },
        {
          icon: "el-icon-reading",
          title: "课程学习",
          status: "在线",
          statusColor: "s-blue",
          desc: "结构化课程资源，支持图文、视频和三维内容一体化呈现。",
          tags: ["结构化", "多媒体", "在线学习"],
        },
        {
          icon: "el-icon-collection",
          title: "案例训练",
          status: "活跃",
          statusColor: "s-blue",
          desc: "围绕典型案例开展决策练习、流程训练和关键岗位模拟。",
          tags: ["案例库", "决策训练", "岗位模拟"],
        },
        {
          icon: "el-icon-document-checked",
          title: "学习记录",
          status: "追踪中",
          statusColor: "s-orange",
          desc: "全程留痕，自动记录学习行为和训练过程，支持后续复盘。",
          tags: ["过程留痕", "自动记录", "可复盘"],
        },
        {
          icon: "el-icon-data-board",
          title: "教学管理",
          status: "可用",
          statusColor: "s-green",
          desc: "教师端组织课程、查看学情、管理资源，形成完整教学闭环。",
          tags: ["多角色", "学情分析", "教学闭环"],
        },
      ],
      panelMetrics: [
        { label: "课程完成率", pct: 78, val: "78%" },
        { label: "案例覆盖率", pct: 92, val: "92%" },
        { label: "留痕完整度", pct: 85, val: "85%" },
      ],
      panelStatus: [
        { name: "情景资源",   tag: "在线",   color: "dot-green"  },
        { name: "案例训练",   tag: "就绪",   color: "dot-blue"   },
        { name: "学习记录",   tag: "追踪",   color: "dot-orange" },
      ],
      featureList: [
        { title: "课程学习", desc: "统一学习入口", icon: "el-icon-reading" },
        { title: "案例训练", desc: "情景化资源组织", icon: "el-icon-collection" },
        { title: "过程留痕", desc: "学习记录与管理", icon: "el-icon-document-checked" },
      ],
      loginRules: {
        username: [{ required: true, trigger: "blur", message: "请输入您的账号" }],
        password: [{ required: true, trigger: "blur", message: "请输入您的密码" }],
        code:     [{ required: true, trigger: "change", message: "请输入验证码" }],
      },
      loading: false,
      captchaEnabled: true,
      register: false,
      redirect: undefined,
    };
  },
  watch: {
    $route: {
      handler(route) {
        this.redirect = route.query && route.query.redirect;
      },
      immediate: true,
    },
  },
  created() {
    this.getCode();
    this.getCookie();
  },
  methods: {
    getCode() {
      getCodeImg().then((res) => {
        this.captchaEnabled =
          res.captchaEnabled === undefined ? true : res.captchaEnabled;
        if (this.captchaEnabled) {
          this.codeUrl = "data:image/gif;base64," + res.img;
          this.loginForm.uuid = res.uuid;
        }
      });
    },
    getCookie() {
      const username = Cookies.get("username");
      const password = Cookies.get("password");
      const rememberMe = Cookies.get("rememberMe");
      this.loginForm = {
        ...this.loginForm,
        username: username === undefined ? this.loginForm.username : username,
        password:
          password === undefined ? this.loginForm.password : decrypt(password),
        rememberMe: rememberMe === undefined ? false : Boolean(rememberMe),
      };
    },
    handleLogin() {
      this.$refs.loginForm.validate((valid) => {
        if (valid) {
          this.loading = true;
          if (this.loginForm.rememberMe) {
            Cookies.set("username", this.loginForm.username, { expires: 30 });
            Cookies.set("password", encrypt(this.loginForm.password), { expires: 30 });
            Cookies.set("rememberMe", this.loginForm.rememberMe, { expires: 30 });
          } else {
            Cookies.remove("username");
            Cookies.remove("password");
            Cookies.remove("rememberMe");
          }
          login(this.loginForm)
            .then((res) => {
              setToken(res.token);
              this.$store.commit("SET_TOKEN", res.token);
              this.$store
                .dispatch("GetInfo")
                .then(() => {
                  if (this.$store.state.roles.includes("teacher")) {
                    this.$router.replace("/teacher");
                  } else {
                    this.$router.replace("/student");
                  }
                })
                .catch(() => {
                  this.loading = false;
                  this.$message.error("获取用户信息失败，请稍后再试");
                });
            })
            .catch(() => {
              this.loading = false;
              if (this.captchaEnabled) {
                this.getCode();
              }
            });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
/* ─────────────────────────────────────────
   Variables
───────────────────────────────────────── */
.login-page {
  --blue-deep: #081c42;
  --blue-mid:  #1557d8;
  --blue-act:  #1e9bff;
  --cyan:      #20c8ff;
  --gray-body: #475569;
  --gray-soft: #64748b;
  position: relative;
  min-height: calc(100vh - 78px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 52px 24px 68px;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 10% 10%, rgba(21,87,216,0.15), transparent 34%),
    radial-gradient(ellipse at 90% 20%, rgba(32,200,255,0.12), transparent 32%),
    linear-gradient(150deg, #f2f7ff 0%, #ecf3ff 50%, #ffffff 100%);
}

/* Backgrounds */
.login-grid-bg {
  position: absolute;
  inset: 0;
  opacity: 0.46;
  background-image:
    linear-gradient(rgba(47,111,237,0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(47,111,237,0.08) 1px, transparent 1px);
  background-size: 44px 44px;
  pointer-events: none;
  mask-image: linear-gradient(180deg, rgba(0,0,0,0.9), transparent 94%);
}
.login-glow {
  position: absolute;
  border-radius: 999px;
  filter: blur(14px);
  pointer-events: none;
}
.login-glow-one   { width: 460px; height: 460px; left: -180px; bottom: -180px; background: rgba(21,87,216,0.12); }
.login-glow-two   { width: 320px; height: 320px; right: 6%; top: 6%; background: rgba(32,200,255,0.10); }
.login-glow-three { width: 240px; height: 240px; right: 30%; bottom: 5%; background: rgba(21,87,216,0.08); }

/* Shell */
.login-shell {
  position: relative;
  z-index: 1;
  width: min(1200px, 100%);
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 48px;
  align-items: start;
}

/* ─────────────────────────────────────────
   LEFT: LOGIN PANEL
───────────────────────────────────────── */
.login-panel {
  position: relative;
  overflow: hidden;
  padding: 28px 34px 24px;
  background: rgba(255,255,255,0.94);
  border: 1px solid rgba(168,199,245,0.78);
  border-radius: 22px;
  box-shadow: 0 24px 56px rgba(8,28,66,0.10);
  backdrop-filter: blur(14px);
}
.tech-login-card::before {
  content: "";
  position: absolute;
  left: 0; right: 0; top: 0;
  height: 3px;
  background: linear-gradient(90deg, #1248c8, #20c8ff, rgba(32,200,255,0));
  border-radius: 22px 22px 0 0;
}

.back-home {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--gray-soft);
  font-size: 12px;
  margin-bottom: 18px;
  text-decoration: none;
  transition: color 0.2s;
  font-weight: 700;
}
.back-home:hover { color: var(--blue-act); }

.brand-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(21,87,216,0.09);
  color: var(--blue-mid);
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 14px;
}
.brand-badge span {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 0 5px rgba(32,200,255,0.18), 0 0 14px rgba(32,200,255,0.7);
}

.login-brand h1 {
  font-size: 23px;
  line-height: 1.28;
  color: var(--blue-deep);
  font-weight: 900;
  margin-bottom: 8px;
}
.login-brand p {
  color: var(--gray-body);
  font-size: 13px;
  line-height: 1.72;
  margin-bottom: 22px;
}

/* Form */
.login-form { margin-top: 2px; }

::v-deep .login-item .el-form-item__label {
  color: #2d4060;
  font-weight: 800;
  font-size: 13px;
  padding-bottom: 6px;
}
::v-deep .login-item .el-input__inner {
  height: 40px;
  border-radius: 10px;
  border-color: #d4e2f4;
  background: #fafcff;
  font-size: 13px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 4px rgba(8,28,66,0.04);
  transition: border-color 0.2s, box-shadow 0.2s;
}
::v-deep .login-item .el-input__inner:focus {
  border-color: #1e9bff;
  box-shadow: 0 0 0 3px rgba(30,155,255,0.12), inset 0 1px 0 rgba(255,255,255,0.9);
}
::v-deep .login-item .el-input__prefix {
  color: #8ba4c8;
}

.captcha-row {
  display: grid;
  grid-template-columns: 1fr 108px;
  gap: 10px;
}
.captcha-img {
  height: 40px;
  border: 1px solid #d4e2f4;
  border-radius: 10px;
  background: #f6f9fc;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s;
  padding: 0;
}
.captcha-img:hover { border-color: #1e9bff; }
.captcha-img img { width: 100%; height: 100%; object-fit: cover; display: block; }

.form-assist {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin: -4px 0 16px;
  color: #8b9dba;
  font-size: 12px;
}

.submit-item { margin-bottom: 14px; }

.login-submit-btn {
  width: 100%;
  height: 42px;
  border-radius: 10px;
  font-weight: 900;
  letter-spacing: 2px;
  font-size: 14px;
  background: linear-gradient(135deg, #1248c8, #1e9bff);
  border: none;
  box-shadow: 0 12px 26px rgba(18,72,200,0.24);
  transition: box-shadow 0.22s, transform 0.22s;
}
.login-submit-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 34px rgba(18,72,200,0.32);
}

.login-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f4f8fe;
  border: 1px solid rgba(168,199,245,0.5);
  color: #6a82a0;
  line-height: 1.6;
  font-size: 11px;
}
.login-note i { color: var(--blue-act); margin-top: 2px; }

/* ─────────────────────────────────────────
   RIGHT: FLOW VISUALIZATION
───────────────────────────────────────── */
.login-visual {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Top copy */
.visual-top { padding-right: 8px; }
.visual-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: var(--blue-mid);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  margin-bottom: 12px;
}
.visual-eyebrow span {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 0 4px rgba(32,200,255,0.18);
}
.visual-top h2 {
  color: var(--blue-deep);
  font-size: 26px;
  line-height: 1.28;
  font-weight: 900;
  margin-bottom: 10px;
}
.visual-top p {
  color: var(--gray-body);
  font-size: 13px;
  line-height: 1.78;
}

/* Flow wrap */
.flow-wrap {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 172px;
  gap: 18px;
  padding: 22px;
  border-radius: 20px;
  background: rgba(255,255,255,0.82);
  border: 1px solid rgba(168,199,245,0.72);
  box-shadow: 0 16px 40px rgba(8,28,66,0.08);
  overflow: hidden;
  backdrop-filter: blur(10px);
}
.flow-bg-grid {
  position: absolute;
  inset: 0;
  opacity: 0.3;
  background-image:
    linear-gradient(rgba(21,87,216,0.10) 1px, transparent 1px),
    linear-gradient(90deg, rgba(21,87,216,0.10) 1px, transparent 1px);
  background-size: 28px 28px;
  pointer-events: none;
}
.flow-bg-glow {
  position: absolute;
  width: 260px; height: 260px;
  right: -60px; top: -60px;
  border-radius: 50%;
  background: rgba(32,200,255,0.08);
  filter: blur(12px);
  pointer-events: none;
}

/* Flow steps */
.flow-steps {
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  z-index: 1;
}
.flow-step {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 14px;
  padding: 12px 10px;
  border-radius: 14px;
  cursor: default;
  transition: background 0.22s;
}
.flow-step:hover,
.flow-step--active {
  background: rgba(21,87,216,0.05);
}

/* Icon column */
.fs-icon-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}
.fs-icon-wrap {
  width: 40px; height: 40px;
  border-radius: 13px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(21,87,216,0.12), rgba(32,200,255,0.14));
  border: 1px solid rgba(21,87,216,0.15);
  color: var(--blue-mid);
  font-size: 18px;
  flex-shrink: 0;
  transition: background 0.22s, box-shadow 0.22s;
}
.flow-step--active .fs-icon-wrap {
  background: linear-gradient(135deg, var(--blue-mid), var(--cyan));
  color: #fff;
  border-color: transparent;
  box-shadow: 0 8px 20px rgba(21,87,216,0.28);
}
.fs-connector {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 0;
  min-height: 20px;
}
.fs-connector-line {
  width: 1px;
  flex: 1;
  background: linear-gradient(180deg, rgba(21,87,216,0.24), rgba(32,200,255,0.18));
}
.fs-connector-dot {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: rgba(32,200,255,0.5);
  margin-top: 2px;
}

/* Step content */
.fs-content { padding-bottom: 4px; }
.fs-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}
.fs-step-num {
  font-size: 11px;
  font-weight: 900;
  color: rgba(21,87,216,0.5);
  font-variant-numeric: tabular-nums;
}
.fs-header h3 {
  color: var(--blue-deep);
  font-size: 14px;
  font-weight: 900;
  margin: 0;
}
.fs-status {
  margin-left: auto;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 999px;
}
.s-green  { background: rgba(74,222,128,0.14); color: #15803d; border: 1px solid rgba(74,222,128,0.32); }
.s-blue   { background: rgba(30,155,255,0.12); color: #1248c8; border: 1px solid rgba(30,155,255,0.28); }
.s-orange { background: rgba(251,191,36,0.14); color: #b45309; border: 1px solid rgba(251,191,36,0.32); }

.fs-content p {
  color: var(--gray-body);
  font-size: 12px;
  line-height: 1.72;
  margin-bottom: 7px;
}
.fs-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.fs-tags span {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(21,87,216,0.07);
  color: var(--blue-mid);
  border: 1px solid rgba(21,87,216,0.12);
}

/* Right panel */
.flow-panel {
  position: relative;
  z-index: 1;
  padding: 16px 14px;
  border-radius: 16px;
  background: linear-gradient(160deg, rgba(8,22,58,0.94), rgba(10,50,108,0.90));
  border: 1px solid rgba(32,200,255,0.22);
  color: #fff;
  box-shadow: 0 14px 30px rgba(4,14,44,0.22);
  align-self: start;
}
.fp-header {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 800;
  color: rgba(255,255,255,0.7);
  margin-bottom: 14px;
  letter-spacing: 0.3px;
}
.fp-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 8px rgba(32,200,255,0.8);
}
.fp-metric { margin-bottom: 12px; }
.fp-label  { display: block; font-size: 10px; color: rgba(255,255,255,0.6); margin-bottom: 5px; font-weight: 700; }
.fp-bar-wrap {
  height: 4px;
  border-radius: 999px;
  background: rgba(255,255,255,0.10);
  margin-bottom: 3px;
  overflow: hidden;
}
.fp-bar {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--blue-act), var(--cyan));
  transition: width 1s ease;
}
.fp-val { font-size: 10px; color: var(--cyan); font-weight: 800; }

.fp-divider { height: 1px; background: rgba(255,255,255,0.09); margin: 14px 0; }

.fp-status-list { display: flex; flex-direction: column; gap: 8px; }
.fp-status-row  { display: flex; align-items: center; gap: 7px; }
.fp-status-dot  {
  width: 6px; height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-green  { background: #4ade80; box-shadow: 0 0 6px rgba(74,222,128,0.7); }
.dot-blue   { background: #60a5fa; box-shadow: 0 0 6px rgba(96,165,250,0.7); }
.dot-orange { background: #fbbf24; box-shadow: 0 0 6px rgba(251,191,36,0.7); }
.fp-status-name { font-size: 11px; color: rgba(255,255,255,0.78); flex: 1; }
.fp-status-tag  { font-size: 10px; color: rgba(255,255,255,0.5); }

/* Feature strip */
.feature-strip {
  display: grid;
  grid-template-columns: repeat(3,1fr);
  gap: 10px;
  padding: 14px;
  background: rgba(255,255,255,0.88);
  border: 1px solid rgba(168,199,245,0.68);
  border-radius: 18px;
  box-shadow: 0 10px 26px rgba(8,28,66,0.06);
}
.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 10px;
  border-radius: 13px;
  background: #f4f8fe;
  border: 1px solid rgba(168,199,245,0.4);
  transition: background 0.2s;
}
.feature-item:hover { background: rgba(21,87,216,0.05); }
.fi-icon {
  width: 34px; height: 34px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(21,87,216,0.11), rgba(32,200,255,0.13));
  color: var(--blue-mid);
  font-size: 16px;
  flex-shrink: 0;
}
.fi-body strong { display: block; color: var(--blue-deep); font-size: 13px; font-weight: 900; margin-bottom: 2px; }
.fi-body span   { color: var(--gray-soft); font-size: 11px; }

/* ─────────────────────────────────────────
   RESPONSIVE
───────────────────────────────────────── */
@media (max-width: 1020px) {
  .login-shell { grid-template-columns: 1fr; }
  .login-visual { display: none; }
}

@media (max-width: 640px) {
  .login-page { padding: 22px 14px 42px; }
  .login-panel { padding: 24px 20px 20px; }
  .login-brand h1 { font-size: 20px; }
  .captcha-row { grid-template-columns: 1fr; }
  .form-assist { align-items: flex-start; flex-direction: column; gap: 6px; }
}
</style>
